import { useEffect, useId, useRef } from 'react';
import { Icon } from '../ui/Icon';
import s from './RedScreen.module.css';

export interface RedScreenProps {
  title: string;
  message: string;
  /** The sticker code, formatted. Shown so it can be read off the phone onto the box. */
  code?: string;
  kicker?: string;
  note?: string;
  /**
   * What the one button says. The default sends the worker back to the boxes, which is right for
   * a refusal about *this* box. A refusal about the whole job — a closed order, the wrong site —
   * has nowhere to carry on to, and says so instead.
   */
  continueLabel?: string;
  onContinue: () => void;
  /**
   * A second way out, for the one refusal a lead may overrule on the spot. It sits under the main
   * button and is never focused automatically — the main button is — and Enter always means the main
   * button, whatever has focus, so the Enter a scanner sends still means "set it aside". Tab or a
   * tap can still focus it; Space or a tap then chooses it.
   */
  secondary?: { label: string; onClick: () => void };
}

/**
 * A refused scan takes the whole screen and holds it until it is tapped. That is the point: a
 * worker at a truck is looking at boxes, not at the phone, and an outcome that can scroll away
 * is an outcome that gets missed. The scanner behind it is stopped while this is up, so the
 * next sticker cannot slip past unread.
 *
 * Whoever renders this must call `announceRefusal` from `./refusalGuard` in the *same* state
 * update as the one that makes `red` (or whatever local state gates this component) truthy —
 * never from inside this component. A sheet elsewhere in the app (the ship sheets, the queue's
 * "Needs attention" sheet) traps focus and stays `aria-hidden`-inverted over the rest of the page
 * for as long as it is open; closing it has to land in the same commit as this component mounting,
 * or its focus trap — still alive until its own close actually commits — wins the fight for focus
 * a beat before it disappears out from under it. See ShipOrder's `showRed` for the pattern.
 */
export function RedScreen({
  title,
  message,
  code,
  kicker = 'Not received',
  note = 'Nothing was changed. Set this box aside.',
  continueLabel = 'Got it, set it aside',
  onContinue,
  secondary,
}: RedScreenProps) {
  const button = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    button.current?.focus();
  }, []);

  // A Bluetooth scanner ends every read with Enter, so Enter dismisses — and is swallowed here,
  // so the read that dismissed the screen is not also taken as the next scan.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      e.stopPropagation();
      onContinue();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onContinue]);

  return (
    <div className={`flash-bad ${s.redScreen}`} role="alertdialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={bodyId}>
      <div className="fb-x">
        <Icon name="x" size={58} />
      </div>
      <div className="fb-kicker">{kicker}</div>
      <h2 className="fb-title" id={titleId}>
        {title}
      </h2>
      <p className="fb-reason" id={bodyId}>
        {message}
      </p>
      {code && (
        <div className="fb-code">
          <small>STICKER</small>
          {code}
        </div>
      )}
      <div className="fb-foot">
        <button type="button" className={`bigbtn white ${s.redContinue}`} ref={button} onClick={onContinue}>
          {continueLabel}
        </button>
        {secondary && (
          <button type="button" className={s.redSecondary} onClick={secondary.onClick}>
            {secondary.label}
          </button>
        )}
        <div className="fb-note">{note}</div>
      </div>
    </div>
  );
}
